<template>
  <div class="add-deliverable">
    <div class="c-info-wrapper">
      <p class="c-info-label">
        <strong>
          {{ t('session.reviewModal.addDeliverableModal.body') }}
        </strong>
      </p>
    </div>
    <template v-if="!isAlreadyUploadedDeliverablePendingRef">
      <FileUpload
        v-if="!noDeliverableRef"
        :disabled="filesRef.length > 0"
        accept=""
        :multiple="true"
        :callback="onDeliverableFilesChange"
      />
      <div v-if="deliverablesFilesRef.length === 0 && filesRef.length === 0" class="no-deliverable-content">
        <CustomCheckbox
          id="no-deliverable-checkbox"
          data-test-id="no-deliverable-checkbox"
          :label="t('session.reviewModal.addDeliverableModal.noDeliverableCheckbox')"
          @emit:change="onNoDeliverableCheckboxChange"
        />
      </div>
    </template>
    <LoadingContent v-if="isAlreadyUploadedDeliverablePendingRef" />
    <div class="files-sent-list" v-else-if="deliverablesFilesRef.length > 0 || filesRef.length > 0">
      <div
        v-for="deliverable in deliverablesFilesRef"
        :key="deliverable.id"
        data-test-id="deliverable-file"
        :class="`file-uploading-container ${isFilesUploadingRef.find((i) => i.fileId === deliverable.id)?.isUploading ? '' : 'file-uploading-container--valid'}`"
      >
        <div class="file-uploading-container__information">
          <FileIcon color="primary" filled />
          <div class="deliverable-information">
            <p class="deliverable-information__name">{{ deliverable.name }}</p>
            <p
              v-if="isFilesUploadingRef.find((i) => i.fileId === deliverable.id)?.isUploading"
              class="deliverable-information__sub-info"
            >
              {{ uploadPercentagesRef.find((i) => i.fileId === deliverable.id)?.percentage }}%
              {{ t('session.reviewModal.addDeliverableModal.sending') }}
            </p>
            <p v-else class="deliverable-information__sub-info">
              {{ t('sesion.reviewModal.addDeliverableModal.received') }}
            </p>
          </div>
        </div>

        <div class="file-uploading-container__status">
          <LoadingIcon
            color="primary"
            v-if="isFilesUploadingRef.find((i) => i.fileId === deliverable.id)?.isUploading"
          />
          <div class="deliverable-remove-btn" @click="removeDeliverableFile(deliverable.id)">
            <CancelIcon data-test-id="deliverable-remove-btn" color="danger" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { watchEffect } from 'vue'
import { toast } from 'vue-sonner'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { checkFileImportationMutation, deleteFileMutation } from '@/features/files/api'
import type { FileType } from '@/features/files/types'
import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  toastFileUploadError,
  uploadFileToPresignedUrl,
} from '@/features/files/utils'
import { getDeliverablePresignedURLMutation, skipDeliverableMutation } from '@/features/sessions/api'
import useDeliverable from '@/features/sessions/hooks/use-deliverable.hook'
import type { GetDeliverablePresignedURLResponseType } from '@/features/sessions/types'
import CustomCheckbox from '@/features/ui/components/checkbox/custom-checkbox.vue'
import FileUpload from '@/features/ui/components/file-upload/file-upload.vue'
import CancelIcon from '@/features/ui/components/icons/cancel-icon.vue'
import FileIcon from '@/features/ui/components/icons/file-icon.vue'
import LoadingIcon from '@/features/ui/components/icons/loading-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import { selectGetReviewSessionModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import { useI18n } from '@/lib/i18n'
import { invalidateDeliverable, invalidateDeliverables, invalidateSession } from '@/utils/lib/vue-query'

const reviewSessionModalRef = selectGetReviewSessionModal(modalsStore)
const { accountRef } = useAccount()
const { t } = useI18n()
const emit = defineEmits(['update:isUploadingRef', 'update:isDisabledRef'])

// the file that the user wants to upload
const filesRef = ref<File[]>([])

// the deliverable that the user has upload or that has been already uploaded
const deliverablesFilesRef = ref<FileType[]>([])

// is the file(s) currently uploading
const isFilesUploadingRef = ref<{ isUploading: boolean; fileId: string }[]>([])

// the percentage of the file that has been uploaded
const uploadPercentagesRef = ref<{ percentage: number; fileId: string }[]>([])

// the current XHR request (file being uploaded)
const xhrsRef = ref<{ xhr: XMLHttpRequest; fileId: string }[]>([])

// checkbox state for not sending a deliverable
const noDeliverableRef = ref<boolean>(false)

// Whenever isUploading or isDisabled changes in AddDeliverable:
// if every file is uploaded, we emit the isUploadingRef to false
watchEffect(() => {
  emit('update:isUploadingRef', isFilesUploadingRef.value.find((i) => i.isUploading)?.isUploading)
})

// The next button should be DISABLED if: no files AND checkbox not checked
// The next button should be ENABLED if: files present OR checkbox checked
watchEffect(() => {
  const hasFiles = deliverablesFilesRef.value.length > 0 || filesRef.value.length > 0
  const shouldDisableButton = !hasFiles && !noDeliverableRef.value
  emit('update:isDisabledRef', !shouldDisableButton) // emit true if button should be enabled (inverted logic for parent)
})

// the deliverables with file(s) that has been uploaded by the user
const { deliverableRef: alreadyUploadedDeliverableRef, isPendingRef: isAlreadyUploadedDeliverablePendingRef } =
  useDeliverable({
    userId: computed(() => accountRef.value?.id),
    sessionId: computed(() => reviewSessionModalRef.value.attachedData.sessionId!),
  })

// check if the deliverables already uploaded has been imported
// if it has been CORRECTLY imported, we can display them
watch(alreadyUploadedDeliverableRef, async (deliverable) => {
  // Initialize checkbox state if skipDeliverable is true
  if (deliverable?.skipDeliverable === true) {
    noDeliverableRef.value = true
  }

  if (deliverable?.folder && filesRef.value.length === 0 && deliverablesFilesRef.value.length === 0) {
    for (const file of deliverable.folder.files) {
      const deliverableImportationData = await checkFileImportation({
        fileId: file?.id,
      })

      if (deliverableImportationData.isImported) {
        deliverablesFilesRef.value.push(file)
      }
    }
  }
})

const { mutateAsync: getDeliverablePresignedURL } = useMutation({
  mutationFn: getDeliverablePresignedURLMutation,
})
const { mutateAsync: checkFileImportation } = useMutation({
  mutationFn: checkFileImportationMutation,
})

const { mutateAsync: skipDeliverable } = useMutation({
  mutationFn: skipDeliverableMutation,
})

const { mutate: deleteDeliverable } = useMutation({
  mutationFn: deleteFileMutation,
  onError: () => {
    toast?.error(t('error.common'))
  },

  onSuccess: async (data, variables) => {
    deliverablesFilesRef.value = deliverablesFilesRef.value.filter((i) => i.id !== variables.fileId)
    await invalidateDeliverable({
      userId: accountRef.value!.id!,
      sessionId: reviewSessionModalRef.value.attachedData.sessionId!,
    })
    // await invalidateSessions({ groupId: selectedGroupIdRef.value! })
    // await invalidateSession(reviewSessionModalRef.value.attachedData.sessionId!)
    toast?.success(t('session.reviewModal.addDeliverableModal.fileDeleted'))
  },
})

async function uploadFileAndTrackProgress(file: File) {
  let uploadData: GetDeliverablePresignedURLResponseType | undefined
  try {
    if (filesRef.value.length === 0) {
      return
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast?.error(t('error.upload.tooLarge', { maxSize: MAX_FILE_SIZE_LABEL }))
      filesRef.value = filesRef.value.filter((i) => i.name !== file.name)
      return
    }

    filesRef.value = filesRef.value.filter((i) => i.name !== file.name)

    uploadData = await getDeliverablePresignedURL({
      mimetype: file.type,
      name: file.name,
      sessionId: reviewSessionModalRef.value.attachedData.sessionId!,
      userId: accountRef.value!.id!,
    })

    deliverablesFilesRef.value = [...deliverablesFilesRef.value, uploadData]
    uploadPercentagesRef.value = [...uploadPercentagesRef.value, { percentage: 0, fileId: uploadData.id }]
    isFilesUploadingRef.value = [...isFilesUploadingRef.value, { isUploading: true, fileId: uploadData.id }]

    await uploadFileToPresignedUrl({
      file,
      url: uploadData.url,
      onProgress: (percentage) => {
        uploadPercentagesRef.value = uploadPercentagesRef.value.map((p) =>
          p.fileId === uploadData!.id ? { ...p, percentage } : p,
        )
      },
      onXhrReady: (xhr) => {
        xhrsRef.value = [...xhrsRef.value, { xhr, fileId: uploadData!.id }]
      },
    })

    await checkFileImportation({ fileId: uploadData.id })

    isFilesUploadingRef.value = isFilesUploadingRef.value.map((uploading) => {
      if (uploading.fileId === uploadData!.id) {
        return { ...uploading, isUploading: false }
      }
      return uploading
    })

    xhrsRef.value = xhrsRef.value.filter((i) => i.fileId !== uploadData!.id)
  } catch (error) {
    if (uploadData) {
      // Best effort : si le réseau est coupé, ce nettoyage échoue aussi. Son
      // erreur ne doit pas sortir du catch, sinon ni le message ni le
      // nettoyage de l'état local n'ont lieu (Sentry WEB_APP-4).
      await checkFileImportation({ fileId: uploadData.folderId }).catch(() => undefined)
      deliverablesFilesRef.value = deliverablesFilesRef.value.filter((i) => i.id !== uploadData!.id)
      xhrsRef.value = xhrsRef.value.filter((i) => i.fileId !== uploadData!.id)
      isFilesUploadingRef.value = isFilesUploadingRef.value.map((uploading) => {
        if (uploading.fileId === uploadData!.id) {
          return { ...uploading, isUploading: false }
        }
        return uploading
      })
    }
    toastFileUploadError(error, toast, t)
    console.error('Error uploading file:', error)
  }
}

// when files are uploaded, we start the upload process
watch(filesRef, () => {
  filesRef.value.forEach((file) => {
    uploadFileAndTrackProgress(file)
  })
})

function onDeliverableFilesChange(file: File) {
  filesRef.value = [...filesRef.value, file]
}

// handle no deliverable checkbox change
async function onNoDeliverableCheckboxChange(checked: boolean) {
  noDeliverableRef.value = checked

  // Call API to mark deliverable as skipped
  try {
    await skipDeliverable({
      sessionId: reviewSessionModalRef.value.attachedData.sessionId!,
      userId: accountRef.value!.id!,
      skip: checked,
    })
    // Invalidate deliverables and session to update pending actions
    await invalidateDeliverables({ sessionId: reviewSessionModalRef.value.attachedData.sessionId! })
    await invalidateSession(reviewSessionModalRef.value.attachedData.sessionId!)
  } catch (error) {
    console.error('Error skipping deliverable:', error)
    toast?.error(t('error.common'))
    // Revert the checkbox state if API call fails
    noDeliverableRef.value = !checked
  }
}

// remove the deliverable file
function removeDeliverableFile(id: string) {
  // if the user is currently uploading a file, we cancel the upload
  if (isFilesUploadingRef.value.find((i) => i.fileId === id)?.isUploading) {
    const xhrToAbort = xhrsRef.value.find((i) => i.fileId === id)
    if (xhrToAbort) {
      xhrToAbort.xhr.abort()
    }
    xhrsRef.value = xhrsRef.value.filter((i) => i.fileId !== id)

    deliverablesFilesRef.value = deliverablesFilesRef.value.filter((i) => i.id !== id)

    isFilesUploadingRef.value = isFilesUploadingRef.value.filter((i) => i.fileId !== id)

    toast?.success(t('session.reviewModal.addDeliverableModal.fileDeleted'))
    return
  }

  if (!deliverablesFilesRef.value) {
    throw new Error('No deliverable to delete')
  }

  // else we delete the file from the server
  deleteDeliverable({
    fileId: id,
  })
}
</script>

<style lang="scss">
@import './add-deliverable.scss';
</style>
